<template>
    <v-card v-if="showEditor && editorContext">
        <material-samples-editor
            :configureSamplesContext="editorContext"
            @cancel="closeEditor"
            @saved="closeEditor"
        />
    </v-card>
    <div v-else>
        <v-card>
            <v-card-title>Material Sample Sets</v-card-title>
            <v-card-text>
                <material-samples-list
                    :sample-sets="sampleSets"
                    search=""
                    @edit-sample-set="editSampleSet"
                    @sample-set-updated="reloadSampleSets"
                    @request-created="reloadRequests"
                />
            </v-card-text>
        </v-card>
        <v-card class="mt-1">
            <v-card-title>Requests</v-card-title>
            <v-card-text>
                <requests-list
                    :requests="requests"
                    search=""
                    @request-updated="reloadRequests"
                />
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import {
    MaterialSamplesContext,
    SavedMaterialSampleSet,
    SavedRequest,
    samplesAPI,
} from "@/services/samples/samplesAPI";
import MaterialSamplesEditor from "../SamplesV2/MaterialSamplesEditor.vue";
import MaterialSamplesList from "../SamplesV2/MaterialSamplesList.vue";
import RequestsList from "../SamplesV2/RequestsList.vue";
import { buildSamplesEditorContext } from "@/lib/samplesV2Utils";
import { withLoadingScreen } from "@/lib/loading";

const { experimentCode, configureSamplesContext } = defineProps<{
    experimentCode: string;
    configureSamplesContext: MaterialSamplesContext | null;
}>();

const sampleSets = ref<SavedMaterialSampleSet[]>([]);
const showEditor = ref(false);
const editorContext = ref<MaterialSamplesContext | null>(configureSamplesContext);
const requests = ref<SavedRequest[]>([]);

onMounted(async () => {
    showEditor.value = !!configureSamplesContext;
    if (showEditor.value) {
        editorContext.value = configureSamplesContext;
    } else {
        await reloadSampleSets();
        await reloadRequests();
    }
});

async function reloadSampleSets() {
    await withLoadingScreen(async () => {
        sampleSets.value = await samplesAPI.fetchSampleSetsByExperiment(experimentCode);
    });
}

async function reloadRequests() {
    await withLoadingScreen(async () => {
        requests.value = await samplesAPI.fetchRequestsByExperiment(experimentCode);
    });
}

async function editSampleSet(sampleSet: SavedMaterialSampleSet) {
    editorContext.value = await buildSamplesEditorContext(sampleSet);
    showEditor.value = true;
}

async function closeEditor() {
    await reloadSampleSets();
    showEditor.value = false;
}

</script>
